import type { ChatAttachment, EnvironmentId } from "@afar-research/contracts";
import { create } from "zustand";

import type {
  ComposerFileAttachment,
  ComposerImageAttachment,
  ComposerThreadTarget,
} from "../composerDraftStore";
import type { AttachmentUploadState } from "./attachmentUploadState";

/**
 * Staging state for the composer's attachments.
 *
 * An AFAR turn carries its attachments inline, so there is nothing to upload
 * ahead of the send and nothing on a server to expire: a staged file is ready
 * the moment it is staged, and releasing it only forgets it here. The queue
 * shape is kept because the composer renders progress, retry and release from
 * it, and because a provider that stages bytes out of band would reintroduce
 * the pending and failed states this already models.
 */

interface AttachmentUploadStore {
  readonly uploadsByImageId: Readonly<Record<string, AttachmentUploadState>>;
}

export const useAttachmentUploadStore = create<AttachmentUploadStore>(() => ({
  uploadsByImageId: {},
}));

export function readAttachmentUpload(imageId: string): AttachmentUploadState | undefined {
  return useAttachmentUploadStore.getState().uploadsByImageId[imageId];
}

function setUploadState(imageId: string, state: AttachmentUploadState): void {
  useAttachmentUploadStore.setState((store) => ({
    uploadsByImageId: { ...store.uploadsByImageId, [imageId]: state },
  }));
}

function clearUploadState(imageId: string): void {
  useAttachmentUploadStore.setState((store) => {
    if (!(imageId in store.uploadsByImageId)) return store;
    const { [imageId]: _removed, ...rest } = store.uploadsByImageId;
    return { uploadsByImageId: rest };
  });
}

/**
 * Marks a staged attachment ready for this environment. The attachment id is
 * the draft's own id: the bytes ride on the turn, so nothing else has to name
 * them.
 */
export function startAttachmentUpload(input: {
  readonly environmentId: EnvironmentId;
  readonly image: ComposerImageAttachment | ComposerFileAttachment;
  /** The draft that owns the file. Unused here: nothing outlives the draft. */
  readonly draftTarget?: ComposerThreadTarget;
}): void {
  setUploadState(input.image.id, {
    status: "ready",
    environmentId: input.environmentId,
    attachmentId: input.image.id,
  });
}

export function releaseAttachmentUpload(imageId: string): void {
  clearUploadState(imageId);
}

export function releasePersistedAttachmentUpload(input: {
  readonly id: string;
  readonly environmentId: EnvironmentId;
  readonly attachmentId: string;
}): void {
  releaseAttachmentUpload(input.id);
}

export function retryAttachmentUpload(input: {
  readonly environmentId: EnvironmentId;
  readonly image: ComposerImageAttachment | ComposerFileAttachment;
  readonly draftTarget?: ComposerThreadTarget;
}): void {
  startAttachmentUpload(input);
}

/**
 * Nothing is stored away from the draft, so a stashed attachment's bytes are
 * present whenever the entry is.
 */
export function verifyStashedAttachmentUpload(_input: {
  readonly environmentId: EnvironmentId;
  readonly attachmentId: string;
}): Promise<{ readonly status: "verified" }> {
  return Promise.resolve({ status: "verified" });
}

/** Staging completes synchronously; kept so send paths can await uniformly. */
export function awaitAttachmentUploads(_imageIds: ReadonlyArray<string>): Promise<void> {
  return Promise.resolve();
}

export function getUploadedAttachments(input: {
  readonly environmentId: EnvironmentId;
  readonly images: ReadonlyArray<ComposerImageAttachment | ComposerFileAttachment>;
}): ChatAttachment[] | null {
  const attachments: ChatAttachment[] = [];
  for (const image of input.images) {
    const upload = readAttachmentUpload(image.id);
    if (upload?.status !== "ready" || upload.environmentId !== input.environmentId) {
      return null;
    }
    if (image.type === "image") {
      // An image's type is required on the wire; a staged one always has it.
      attachments.push({
        type: "image",
        id: upload.attachmentId,
        name: image.name,
        mimeType: image.mimeType,
        sizeBytes: image.sizeBytes,
        ...(image.source ? { source: image.source } : {}),
      });
      continue;
    }
    attachments.push({
      type: "file",
      id: upload.attachmentId,
      name: image.name,
      sizeBytes: image.sizeBytes,
      ...(image.mimeType ? { mimeType: image.mimeType } : {}),
      ...(image.source ? { source: image.source } : {}),
    });
  }
  return attachments;
}

export function releaseDraftAttachment(
  attachment: ComposerImageAttachment | ComposerFileAttachment,
): void {
  releaseAttachmentUpload(attachment.id);
}

export function releaseDraftAttachments(
  attachments: ReadonlyArray<ComposerImageAttachment | ComposerFileAttachment>,
): void {
  for (const attachment of attachments) {
    releaseDraftAttachment(attachment);
  }
}
